// Lazy thumbnail for an uploaded file: images → scaled <img>, PDFs → first page via pdf.js, otherwise an extension badge.
import React, { useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { FileText } from 'lucide-react';

const cache = new Map();
const IMG = /\.(png|jpe?g|webp|gif|heic)$/i;

export function FileThumb({ file, fetchBlob, size = 44, hero = false }) {
  const key = `${file.docId}-${file.idx}-${file.version}`;
  const [src, setSrc] = useState(cache.get(key) || null);
  const [failed, setFailed] = useState(false);
  const name = file.original_filename || '';
  const ext = (name.split('.').pop() || 'FILE').toUpperCase().slice(0, 4);
  const isPdf = /\.pdf$/i.test(name) || file.content_type === 'application/pdf';
  const isImg = IMG.test(name) || (file.content_type || '').startsWith('image/');
  useEffect(() => {
    if (src || failed || (!isPdf && !isImg) || (file.size || 0) < 100) return undefined;
    let alive = true;
    (async () => {
      try {
        const blob = await fetchBlob(file);
        if (isImg) { const u = URL.createObjectURL(blob); cache.set(key, u); if (alive) setSrc(u); return; }
        const pdf = await pdfjsLib.getDocument({ data: await blob.arrayBuffer() }).promise;
        const page = await pdf.getPage(1); const vp = page.getViewport({ scale: 1 }); const scale = (size * 2) / vp.width; const v = page.getViewport({ scale });
        const c = document.createElement('canvas'); c.width = v.width; c.height = v.height;
        await page.render({ canvasContext: c.getContext('2d'), viewport: v }).promise;
        const u = c.toDataURL('image/jpeg', 0.7); cache.set(key, u); if (alive) setSrc(u); pdf.destroy?.();
      } catch { if (alive) setFailed(true); }
    })();
    return () => { alive = false; };
  }, [key, src, failed, isPdf, isImg, fetchBlob, file, size]);
  const radius = size >= 100 ? 14 : size >= 56 ? 12 : 10;
  return (
    <span className={`grid shrink-0 place-items-center overflow-hidden bg-white ring-1 ring-black/[0.08] ${hero ? 'shadow-[0_2px_4px_rgba(0,0,0,0.04),0_18px_36px_-16px_rgba(0,0,0,0.25)]' : 'shadow-[0_1px_2px_rgba(0,0,0,0.05)]'}`} style={{ width: size, height: size, borderRadius: radius }} data-testid="file-thumb">
      {src ? <img src={src} alt="" className="h-full w-full object-cover object-top" /> : (
        <span className="flex flex-col items-center" style={{ gap: size >= 100 ? 6 : 2 }}>
          <FileText className={isPdf ? 'text-[#FF3B30]' : isImg ? 'text-[#007AFF]' : 'text-[#8E8E93]'} style={{ width: size * 0.36, height: size * 0.36 }} strokeWidth={1.5} />
          <span className={`font-semibold tracking-wide text-[#6e6e73] ${size >= 100 ? 'text-[12px]' : 'text-[9px]'}`}>{ext}</span>
        </span>)}
    </span>
  );
}
